import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, UserMinus } from "lucide-react";
import { http } from "@/lib/api";
import { ROLE_NAMES, day, useLoad, when } from "@/lib/staff";
import { Card, ErrorBox, Field, PageHead, Pager, Row, RoleBadge, Select, Skeleton, Table } from "@/components/staff/ui";
import { AuditChange, actionLabel } from "@/components/staff/bits";
import { ConfirmAction } from "@/components/staff/ConfirmAction";
import { VerifyStatus } from "@/components/staff/VerifyIdentity";
import { PermissionPicker, permLabels, startingTicks } from "@/components/staff/PermissionPicker";

const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

function lockedReason(m) {
  if (m.role === "owner") return "The owner can always do everything, and that can't be changed.";
  if (m.role === "super_admin") return "Only the owner can change a Super Admin's role or permissions.";
  return "You can't change your own access, or you don't have the Manage Admins permission.";
}

function Details({ m }) {
  return (
    <Card title="Details" testid="member-details">
      <Row label="Role" testid="member-role">{ROLE_NAMES[m.role]}</Row>
      <Row label="Added" testid="member-added">{m.role === "owner" ? "—" : day(m.since)}</Row>
      <Row label="Added by" testid="member-added-by">{m.granted_by || "—"}</Row>
      <Row label="Last active" testid="member-last-active">{when(m.last_seen_at)}</Row>
      <Row label="Permissions" testid="member-perm-count">{m.role === "owner" ? "Everything" : m.permissions.length}</Row>
    </Card>
  );
}

function AccessEditor({ m, catalog, onSaved }) {
  const navigate = useNavigate();
  const [role, setRole] = useState(m.role);
  const [perms, setPerms] = useState(m.permissions);
  useEffect(() => { setRole(m.role); setPerms(m.permissions); }, [m]);
  const labels = permLabels(catalog);
  const added = perms.filter((p) => !m.permissions.includes(p));
  const removed = m.permissions.filter((p) => !perms.includes(p));
  const changed = role !== m.role || !same(perms, m.permissions);
  const roleOptions = catalog.roles.map((r) => [r.value, r.label]);
  return (
    <Card title="Role and permissions" testid="member-access">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <Field label="Role">
          <Select value={role} onChange={(r) => { setRole(r); setPerms(startingTicks(catalog, r)); }} options={roleOptions} label="Role" testid="member-role-select" />
        </Field>
        <button type="button" onClick={() => setPerms(startingTicks(catalog, role))} className="h-9 text-xs text-sky-300 hover:underline" data-testid="member-reset-ticks">
          Use the starting ticks for this role
        </button>
      </div>
      <PermissionPicker catalog={catalog} value={perms} onChange={setPerms} testid="member-perm" />
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <ConfirmAction label="Remove from staff" icon={UserMinus} danger needsVerify testid="member-remove-btn" confirmLabel="Remove from staff"
          title={`Remove ${m.email} from the staff team?`} description="They keep their student account and plan, but lose the staff area straight away."
          run={async (reason, token) => { await http.post(`/staff/team/${m.user_id}/remove`, { reason, reauth_token: token }); toast.success("Removed from staff"); navigate("/staff/team"); }} />
        <ConfirmAction label="Save changes" icon={Save} primary needsVerify disabled={!changed} testid="member-save-btn" confirmLabel="Save changes"
          title={`Change ${m.email}'s access?`}
          description={[role !== m.role && `Role: ${ROLE_NAMES[m.role]} to ${ROLE_NAMES[role]}.`, added.length && `Adds: ${added.map((p) => labels[p]).join(", ")}.`,
            removed.length && `Takes away: ${removed.map((p) => labels[p]).join(", ")}.`].filter(Boolean).join(" ")}
          run={async (reason, token) => { await http.put(`/staff/team/${m.user_id}`, { role, permissions: perms, reason, reauth_token: token }); toast.success("Access updated"); onSaved(); }} />
      </div>
    </Card>
  );
}

function ReadOnlyAccess({ m, catalog }) {
  const all = useMemo(() => catalog.groups.flatMap((g) => g.items.map((i) => i.key)), [catalog]);
  return (
    <Card title="Role and permissions" testid="member-access-readonly">
      <p className="text-xs text-neutral-400 mb-4" data-testid="member-locked-reason">{lockedReason(m)}</p>
      <PermissionPicker catalog={catalog} value={m.role === "owner" ? all : m.permissions} onChange={() => {}} readOnly testid="member-perm" />
    </Card>
  );
}

function History({ rows, labels }) {
  return (
    <Card title="Changes to their access" testid="member-access-history">
      {!rows.length && <p className="text-sm text-neutral-500">No changes yet.</p>}
      <ul className="space-y-3">
        {rows.map((a) => (
          <li key={a.audit_id} className="text-sm" data-testid={`member-history-${a.audit_id}`}>
            <p><span className="text-neutral-100">{actionLabel(a.action)}</span> <span className="text-xs text-neutral-500">· {a.actor_email} · {when(a.at)}</span></p>
            {a.reason && <p className="text-xs text-neutral-400 mt-0.5">"{a.reason}"</p>}
            {a.meta?.added?.length > 0 && <p className="text-xs text-emerald-300 mt-0.5">Added: {a.meta.added.map((p) => labels[p] || p).join(", ")}</p>}
            {a.meta?.removed?.length > 0 && <p className="text-xs text-red-300 mt-0.5">Taken away: {a.meta.removed.map((p) => labels[p] || p).join(", ")}</p>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const ACTIVITY_COLS = [
  { key: "at", label: "When", render: (a) => <span className="whitespace-nowrap text-xs">{when(a.at)}</span> },
  { key: "action", label: "Action", render: (a) => <span className="text-sm text-neutral-100">{actionLabel(a.action)}</span> },
  { key: "target", label: "Account", render: (a) => (a.target_id ? <Link to={`/staff/students/${a.target_id}`} className="text-xs text-sky-300 hover:underline">{a.target_email || "Open"}</Link> : <span className="text-xs text-neutral-600">—</span>) },
  { key: "reason", label: "Reason", className: "max-w-[280px]", render: (a) => <span className="text-xs text-neutral-300">{a.reason || "—"}</span> },
  { key: "change", label: "Change", className: "max-w-[240px]", render: (a) => <AuditChange a={a} /> },
];

function Activity({ d, page, setPage }) {
  if (!d.can_see_activity) return <Card title="What they've done" testid="member-activity"><p className="text-sm text-neutral-500">Needs the "See the audit log" permission.</p></Card>;
  return (
    <div data-testid="member-activity">
      <h2 className="text-sm font-medium text-neutral-200 mb-3">What they've done in the staff area</h2>
      <Table testid="member-activity-table" columns={ACTIVITY_COLS} rows={d.activity.items} rowKey={(a) => a.audit_id} empty="Nothing yet." />
      <Pager page={page} pageSize={d.activity.page_size} total={d.activity.total} onPage={setPage} testid="member-activity-pager" />
    </div>
  );
}

export default function StaffMemberPage() {
  const { uid } = useParams();
  const [page, setPage] = useState(1);
  const params = useMemo(() => ({ page }), [page]);
  const { data: d, error, loading, reload } = useLoad(`/staff/team/${uid}`, params);
  return (
    <div data-testid="staff-member" className="space-y-5">
      <Link to="/staff/team" className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white" data-testid="member-back-link"><ArrowLeft className="h-4 w-4" />Staff & Access</Link>
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!d && loading && <Skeleton rows={8} />}
      {d && (
        <>
          <PageHead title={d.member.name || d.member.email} sub={d.member.email}>
            <RoleBadge role={d.member.role} testid="member-role-badge" />
            {d.member.can_edit && <VerifyStatus />}
          </PageHead>
          <div className="grid xl:grid-cols-3 gap-4 items-start">
            <div className="xl:col-span-2">
              {d.member.can_edit ? <AccessEditor m={d.member} catalog={d.catalog} onSaved={reload} /> : <ReadOnlyAccess m={d.member} catalog={d.catalog} />}
            </div>
            <div className="space-y-4">
              <Details m={d.member} />
              {d.can_see_activity && <History rows={d.access_history} labels={permLabels(d.catalog)} />}
            </div>
          </div>
          <Activity d={d} page={page} setPage={setPage} />
        </>
      )}
    </div>
  );
}
